import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { FrontdeskService } from '../../services/frontdesk/frontdesk.service';
import { ToastService } from '../../services/toast/toast.service';
import { downloadBookingSlip } from '../../common-files/pdf';
import { LoaderComponent } from '../../atoms/loader/loader.component';

@Component({
  selector: 'app-frontdesk-booking-search',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, LoaderComponent],
  templateUrl: './frontdesk-booking-search.component.html',
  styleUrls: ['./frontdesk-booking-search.component.scss', '../../../common-style/dropdown.scss'],
})
export class FrontdeskBookingSearchComponent {
  searchType = new FormControl<'aadhaar' | 'id'>('aadhaar', { nonNullable: true });
  searchValue = new FormControl('');

  results: any[] = [];
  hasSearched: boolean = false;
  isLoading: boolean = false;

  constructor(
    private frontdeskService: FrontdeskService,
    private toastService: ToastService
  ) {}

  search(): void {
    const value = (this.searchValue.value || '').trim();
    if (!value) {
      this.toastService.error('Enter a value to search.');
      return;
    }

    this.isLoading = true;
    const done = (rows: any[]) => {
      this.results = rows;
      this.hasSearched = true;
      this.isLoading = false;
    };
    const fail = (err: any) => {
      this.results = [];
      this.hasSearched = true;
      this.isLoading = false;
      this.toastService.error(err?.error?.message || 'Search failed.');
    };

    if (this.searchType.value === 'id') {
      this.frontdeskService.searchBookingById(Number(value)).subscribe({
        // by-id returns a single requisition object (spread at top level)
        next: (res: any) => done(res?.data?.requisitionId ? [res.data] : []),
        error: fail,
      });
    } else {
      this.frontdeskService.searchBookingByAadhaar(value).subscribe({
        next: (res: any) => done(res?.data?.requisitionDetails || []),
        error: fail,
      });
    }
  }

  canResend(row: any): boolean {
    return row.requisitionStatus === 'CONFIRMED';
  }

  resend(row: any): void {
    this.isLoading = true;
    this.frontdeskService.resendConfirmationSms(row.requisitionId).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        this.toastService.success(res?.message || 'Confirmation SMS sent.');
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to send SMS.');
      },
    });
  }

  // download the booking slip as a PDF (fetches the full detail first)
  downloadSlip(row: any): void {
    this.isLoading = true;
    this.frontdeskService.searchBookingById(Number(row.requisitionId)).subscribe({
      next: async (res: any) => {
        this.isLoading = false;
        if (res?.data?.requisitionId) await downloadBookingSlip(res.data);
        else this.toastService.error('Could not load the booking detail.');
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to build the slip.');
      },
    });
  }
}
